import {
  type AcademicTerm,
  academicYearTerms,
  hongKongDate,
  type SchoolYear,
  schoolYearStart,
  validateAcademicTerms,
} from "@classroom/domain";
import { useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";

function nextDate(value: string) {
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export default function TermSettings({
  terms,
  onSave,
  active,
  schoolYear,
  readOnly = false,
  annualMode = false,
}: {
  terms: AcademicTerm[];
  onSave: (terms: AcademicTerm[]) => Promise<void>;
  active: boolean;
  schoolYear?: SchoolYear;
  readOnly?: boolean;
  annualMode?: boolean;
}) {
  const { t } = useTranslation("terms");
  const { t: yearText } = useTranslation("schoolYears");
  const currentYear = schoolYearStart(hongKongDate());
  const settingsYear = Number(schoolYear?.startDate?.slice(0, 4) || schoolYear?.name.slice(0, 4)) || currentYear;
  const [draft, setDraft] = useState(() => terms.map((term) => ({ ...term })));
  const [year, setYear] = useState(settingsYear);
  const [termCount, setTermCount] = useState<2 | 3>(2);
  const [boundaries, setBoundaries] = useState<Partial<Record<number, string>>>({});
  const [setupOpen, setSetupOpen] = useState(
    () =>
      !annualMode &&
      !readOnly &&
      !schoolYear &&
      !terms.some((term) => term.schoolYear === `${currentYear}–${currentYear + 1}`),
  );
  const [setupError, setSetupError] = useState("");
  const setupButton = useRef<HTMLButtonElement>(null);
  const setupRef = useDialogFocus<HTMLElement>(
    setupOpen && active,
    () => setSetupOpen(false),
    () => setupButton.current,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const template = academicYearTerms(year, termCount);
  template.forEach((term, index) => {
    if (index < template.length - 1) term.endDate = boundaries[index] ?? term.endDate;
    if (index) term.startDate = nextDate(template[index - 1]!.endDate);
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(terms);
  const update = (id: string, patch: Partial<AcademicTerm>) => {
    setDraft((current) => current.map((term) => (term.id === id ? { ...term, ...patch } : term)));
    setSaved(false);
    setError("");
  };
  function addYear() {
    if (!Number.isInteger(year) || year < 1900 || year > 9998) {
      setSetupError(t("errors.yearInvalid"));
      return;
    }
    const next = template;
    if (!schoolYear && draft.some((term) => term.schoolYear === next[0]!.schoolYear)) {
      setSetupError(t("errors.yearExists"));
      return;
    }
    const issue = validateAcademicTerms(next);
    if (issue) {
      setSetupError(t(`errors.${issue}`));
      return;
    }
    setDraft((current) =>
      schoolYear ? next.map((term) => ({ ...term, schoolYear: schoolYear.name })) : [...current, ...next],
    );
    setError("");
    setSaved(false);
    setSetupError("");
    setSetupOpen(false);
  }
  return (
    <>
      <form
        className="term-settings"
        aria-label={t("settingsTitle")}
        data-save-state={busy ? "saving" : dirty ? "dirty" : "saved"}
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy || readOnly) return;
          const next = draft.map((term) => ({ ...term, name: term.name.trim(), schoolYear: term.schoolYear.trim() }));
          const issue = validateAcademicTerms(next);
          if (issue) {
            setError(t(`errors.${issue}`));
            return;
          }
          setBusy(true);
          setError("");
          setSaved(false);
          try {
            await onSave(next);
            setDraft(next);
            setSaved(true);
          } catch (caught) {
            setError(caught instanceof Error ? caught.message : t("errors.save"));
          } finally {
            setBusy(false);
          }
        }}
      >
        <header className="paper-card term-settings-intro">
          <div>
            <span className="grade-eyebrow">{t("settingsEyebrow")}</span>
            <h2>{t("settingsTitle")}</h2>
          </div>
          {(!annualMode || schoolYear) && (
            <button
              ref={setupButton}
              type="button"
              className="secondary-button term-button"
              disabled={busy || readOnly}
              onClick={() => {
                let nextYear = settingsYear;
                if (!schoolYear)
                  while (draft.some((term) => term.schoolYear === `${nextYear}–${nextYear + 1}`)) nextYear++;
                setYear(nextYear);
                setBoundaries({});
                setSetupError("");
                setSetupOpen(true);
              }}
            >
              {schoolYear ? yearText("termSetup") : t(draft.length ? "addYear" : "setupYear")}
            </button>
          )}
        </header>
        <fieldset disabled={busy || readOnly} className="term-settings-list">
          <legend className="sr-only">{t("settingsTitle")}</legend>
          {draft.map((term, index) => (
            <section className="paper-card term-setting-row" key={term.id}>
              <span className="term-index">{String(index + 1).padStart(2, "0")}</span>
              <label>
                {t("schoolYear")}
                <input
                  aria-label={`${t("schoolYear")} ${index + 1}`}
                  value={term.schoolYear}
                  onChange={(event) => update(term.id, { schoolYear: event.target.value })}
                  maxLength={40}
                  required
                  readOnly={Boolean(schoolYear)}
                />
              </label>
              <label>
                {t("name")}
                <input
                  aria-label={`${t("name")} ${index + 1}`}
                  value={term.name}
                  onChange={(event) => update(term.id, { name: event.target.value })}
                  maxLength={60}
                  required
                />
              </label>
              <label>
                {t("start")}
                <input
                  type="date"
                  aria-label={`${t("start")} ${index + 1}`}
                  value={term.startDate}
                  onChange={(event) => update(term.id, { startDate: event.target.value })}
                  required
                />
              </label>
              <label>
                {t("end")}
                <input
                  type="date"
                  aria-label={`${t("end")} ${index + 1}`}
                  value={term.endDate}
                  onChange={(event) => update(term.id, { endDate: event.target.value })}
                  required
                />
              </label>
              <button
                type="button"
                className="danger-text"
                aria-label={t("remove", { name: `${term.schoolYear} ${term.name}` })}
                onClick={() => {
                  setDraft((current) => current.filter((item) => item.id !== term.id));
                  setSaved(false);
                }}
              >
                ×
              </button>
            </section>
          ))}
        </fieldset>
        <button
          type="button"
          className="secondary-button term-button"
          disabled={busy || readOnly}
          onClick={() => {
            setDraft((current) => [
              ...current,
              {
                id: crypto.randomUUID(),
                schoolYear: schoolYear?.name || `${year}–${year + 1}`,
                name: "",
                startDate: "",
                endDate: "",
              },
            ]);
            setSaved(false);
          }}
        >
          {t("addTerm")}
        </button>
        <div className="term-settings-footer">
          <button type="submit" className="primary-action" disabled={busy || readOnly || !dirty}>
            {t(busy ? "saving" : "save")}
          </button>
          <button
            type="button"
            disabled={busy || readOnly || !dirty}
            onClick={() => {
              setDraft(terms.map((term) => ({ ...term })));
              setError("");
              setSaved(false);
            }}
          >
            {t("reset")}
          </button>
          {saved && <span role="status">{t("saved")}</span>}
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </form>
      {setupOpen && active && (
        <div className="record-export-backdrop">
          <section
            ref={setupRef}
            tabIndex={-1}
            className="paper-card term-setup-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="term-setup-title"
          >
            <header>
              <h2 id="term-setup-title">{t("setupYear")}</h2>
              <button type="button" aria-label={t("closeSetup")} onClick={() => setSetupOpen(false)}>
                ×
              </button>
            </header>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                addYear();
              }}
            >
              <label className="term-setup-year">
                {t("yearStart")}
                <input
                  type="number"
                  min="1900"
                  max="9998"
                  required
                  value={year}
                  readOnly={Boolean(schoolYear)}
                  data-dialog-initial-focus
                  onChange={(event) => {
                    setYear(Number(event.target.value));
                    setBoundaries({});
                    setSetupError("");
                  }}
                />
              </label>
              <p className="term-setup-range">{t("academicYearRange", { year, nextYear: year + 1 })}</p>
              <fieldset className="term-setup-options">
                <legend>{t("termCount")}</legend>
                {([2, 3] as const).map((count) => (
                  <label key={count}>
                    <input
                      type="radio"
                      name="term-count"
                      value={count}
                      checked={termCount === count}
                      onChange={() => {
                        setTermCount(count);
                        setBoundaries({});
                        setSetupError("");
                      }}
                    />
                    <span>{t(count === 2 ? "twoTerms" : "threeTerms")}</span>
                  </label>
                ))}
              </fieldset>
              <div className="term-setup-preview" aria-label={t("templatePreview")}>
                {template.map((term, index) => (
                  <div key={term.id}>
                    <strong>{term.name}</strong>
                    <label>
                      {t("start")}
                      <input
                        type="date"
                        readOnly
                        value={term.startDate}
                        aria-label={t("templateStart", { name: term.name })}
                      />
                    </label>
                    <label>
                      {t("end")}
                      <input
                        type="date"
                        required
                        value={term.endDate}
                        readOnly={index === template.length - 1}
                        min={`${year}-09-01`}
                        max={`${year + 1}-08-31`}
                        aria-label={t("templateEnd", { name: term.name })}
                        onChange={(event) => {
                          setBoundaries((current) => ({ ...current, [index]: event.target.value }));
                          setSetupError("");
                        }}
                      />
                    </label>
                  </div>
                ))}
              </div>
              {setupError && (
                <p className="form-error" role="alert">
                  {setupError}
                </p>
              )}
              <footer>
                <button type="button" onClick={() => setSetupOpen(false)}>
                  {t("setupLater")}
                </button>
                <button type="submit" className="primary-action">
                  {t("createTerms")}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </>
  );
}
